import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Pill,
  Loader2,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Plus,
  Clock,
  Pencil,
  Lock,
  Sparkles,
} from "lucide-react";
import MedicationEditorModal from "@/components/mar/MedicationEditorModal";
import VoiceMARModal from "@/components/VoiceMARModal";

const STATUS_META = {
  given: { label: "Given", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10 border-emerald-500/30" },
  refused: { label: "Refused", icon: XCircle, color: "text-destructive", bg: "bg-destructive/10 border-destructive/30" },
  held: { label: "Held", icon: PauseCircle, color: "text-amber-600", bg: "bg-amber-500/10 border-amber-500/30" },
  missed: { label: "Missed", icon: XCircle, color: "text-destructive", bg: "bg-destructive/10 border-destructive/30" },
  pending: { label: "Pending", icon: Clock, color: "text-clinical-subtext", bg: "bg-clinical-bg border-clinical-border" },
};

const ACTION_BUTTONS = [
  { status: "given", label: "Given", icon: CheckCircle2, hot: "hover:bg-emerald-500/10 hover:text-emerald-700 hover:border-emerald-500/40" },
  { status: "refused", label: "Refused", icon: XCircle, hot: "hover:bg-destructive/10 hover:text-destructive hover:border-destructive/40" },
  { status: "held", label: "Hold", icon: PauseCircle, hot: "hover:bg-amber-500/10 hover:text-amber-700 hover:border-amber-500/40" },
];

const emptyForm = {
  name: "",
  dosage: "",
  route: "PO",
  instructions: "",
  scheduled_times: "",
  is_prn: false,
};

/**
 * MARGrid — renders today's medication doses for a client.
 * - Caregivers can tap Given/Refused/Held on each dose
 * - Caregivers can add/edit/delete medications themselves via the modal
 * - readOnly disables all writes (used by AdminMAR history view)
 */
export default function MARGrid({ clientId, dateStr, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // key -> bool

  // medication editor state
  const [editing, setEditing] = useState(null); // null | "new" | medication object
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [voiceMAROpen, setVoiceMAROpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = dateStr ? { date: dateStr } : {};
      const { data } = await api.get(`/clients/${clientId}/mar`, { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [clientId, dateStr]);

  useEffect(() => { load(); }, [load]);

  const log = async (dose, status) => {
    if (readOnly) return;
    const key = `${dose.medication.id}-${dose.scheduled_time}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post("/mar/log", {
        medication_id: dose.medication.id,
        scheduled_date: data.date,
        scheduled_time: dose.scheduled_time,
        status,
      });
      toast.success(`${dose.medication.name} — ${STATUS_META[status].label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log dose");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const openEdit = (med) => {
    setForm({
      name: med.name,
      dosage: med.dosage,
      route: med.route || "PO",
      instructions: med.instructions || "",
      scheduled_times: (med.scheduled_times || []).join(", "),
      is_prn: !!med.is_prn,
    });
    setEditing(med);
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim() || !form.dosage.trim()) {
      toast.error("Name and dosage are required");
      return;
    }
    const times = form.scheduled_times
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!form.is_prn && times.length === 0) {
      toast.error("Add at least one scheduled time (HH:MM), or mark as PRN");
      return;
    }
    const bad = times.find((t) => !/^\d{1,2}:\d{2}$/.test(t));
    if (bad) {
      toast.error(`Invalid time "${bad}". Use HH:MM`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        dosage: form.dosage.trim(),
        route: form.route,
        instructions: form.instructions.trim(),
        scheduled_times: times,
        is_prn: form.is_prn,
      };
      if (editing === "new") {
        await api.post(`/clients/${clientId}/medications`, payload);
        toast.success(`${payload.name} added`);
      } else {
        await api.put(`/medications/${editing.id}`, payload);
        toast.success(`${payload.name} updated`);
      }
      close();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (editing === "new" || !editing?.id) return;
    if (!window.confirm(`Remove ${editing.name} from this client's medication list? Past records stay intact.`)) return;
    setSaving(true);
    try {
      await api.delete(`/medications/${editing.id}`);
      toast.success(`${editing.name} removed`);
      close();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-clinical-subtext py-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading medications…
      </div>
    );
  }

  // When meds are locked by admin AND the user is a caregiver, they shouldn't be
  // able to write. We don't know the user's role here directly — use localStorage.
  let userRole = "caregiver";
  try {
    userRole = JSON.parse(localStorage.getItem("hc_user_profile_cache") || "{}").role || "caregiver";
  } catch (_) { /* default */ }
  const adminLocked = !!data?.client?.meds_locked;
  const writesDisabled = readOnly || (adminLocked && userRole !== "admin");

  // Build the per-row list & detect first occurrence of each medication
  // so we render edit/delete actions only once per med (even if it has multiple times).
  const seenMedIds = new Set();

  return (
    <div className="space-y-2.5" data-testid="mar-grid">
      {adminLocked && userRole !== "admin" && (
        <div
          className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800"
          data-testid="meds-locked-banner"
        >
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            The agency has locked medications for this client. You can log doses below,
            but only an admin can add, edit, or remove medications.
          </span>
        </div>
      )}

      {(!data || data.doses.length === 0) && (
        <div className="rounded-xl border border-dashed border-clinical-border p-5 text-center text-sm text-clinical-subtext">
          <Pill className="h-5 w-5 mx-auto mb-1" />
          No active medications scheduled for this client.
        </div>
      )}

      {data?.doses.map((dose, idx) => {
        const key = `${dose.medication.id}-${dose.scheduled_time}-${idx}`;
        const meta = STATUS_META[dose.status] || STATUS_META.pending;
        const StatusIcon = meta.icon;
        const isBusy = busy[`${dose.medication.id}-${dose.scheduled_time}`];
        const isLogged = dose.status !== "pending";
        const isPRN = dose.is_prn;
        const isFirstOfMed = !seenMedIds.has(dose.medication.id);
        seenMedIds.add(dose.medication.id);

        return (
          <div
            key={key}
            className={`rounded-xl border p-3 transition-colors ${
              isLogged ? meta.bg : "bg-white border-clinical-border"
            }`}
            data-testid={`mar-dose-${idx}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-clinical-primary/10 text-clinical-primary">
                <Pill className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-clinical-text truncate">
                    {dose.medication.name}
                  </span>
                  <span className="pill bg-clinical-bg text-clinical-subtext">
                    {dose.medication.dosage}
                  </span>
                  {dose.medication.route && dose.medication.route !== "PO" && (
                    <span className="pill bg-clinical-bg text-clinical-subtext">
                      {dose.medication.route}
                    </span>
                  )}
                  {isPRN && (
                    <span className="pill bg-clinical-accent/10 text-clinical-accent">PRN</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-clinical-subtext">
                  <Clock className="h-3 w-3" />
                  {dose.scheduled_time}
                  {dose.medication.instructions && (
                    <span className="truncate"> · {dose.medication.instructions}</span>
                  )}
                </div>
                {dose.entry && (
                  <div className="mt-1 text-xs">
                    <span className={`inline-flex items-center gap-1 font-medium ${meta.color}`}>
                      <StatusIcon className="h-3 w-3" /> {meta.label}
                    </span>
                    <span className="text-clinical-subtext">
                      {" · "}by {dose.entry.caregiver_name?.split(" ")[0]}{" "}
                      {new Date(dose.entry.administered_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {!writesDisabled && isFirstOfMed && (
                <button
                  type="button"
                  onClick={() => openEdit(dose.medication)}
                  className="btn-ghost p-1.5"
                  data-testid={`edit-med-${dose.medication.id}`}
                  title="Edit medication"
                  aria-label={`Edit ${dose.medication.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {dose.medication.updated_at && dose.medication.updated_by_name && isFirstOfMed && (
              <div className="mt-1 pl-12 text-[11px] text-clinical-subtext italic">
                Last edited by {dose.medication.updated_by_name} ·{" "}
                {new Date(dose.medication.updated_at).toLocaleString([], {
                  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </div>
            )}

            {!readOnly && (
              <div className="mt-3 flex gap-1.5 flex-wrap">
                {ACTION_BUTTONS.map((b) => {
                  const isActive = dose.status === b.status;
                  return (
                    <button
                      key={b.status}
                      type="button"
                      onClick={() => log(dose, b.status)}
                      disabled={isBusy}
                      data-testid={`mar-action-${b.status}-${idx}`}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? `${meta.bg} ${meta.color}`
                          : `bg-white border-clinical-border text-clinical-subtext ${b.hot}`
                      } disabled:opacity-50`}
                    >
                      {isBusy && isActive ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <b.icon className="h-3 w-3" />
                      )}
                      {b.label}
                    </button>
                  );
                })}
                {isPRN && dose.status === "pending" && (
                  <span className="ml-auto text-[11px] text-clinical-subtext flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Tap Given to log a PRN dose
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!writesDisabled && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setVoiceMAROpen(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-clinical-accent px-3 py-3 text-sm font-semibold text-white transition hover:opacity-90"
            data-testid="voice-mar-open"
          >
            <Sparkles className="h-4 w-4" /> Voice log doses
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-clinical-border bg-white px-3 py-3 text-sm font-medium text-clinical-primary transition-colors hover:border-clinical-primary/50 hover:bg-clinical-primary/5"
            data-testid="add-medication-button"
          >
            <Plus className="h-4 w-4" /> Add medication
          </button>
        </div>
      )}

      {/* Medication editor modal */}
      {editing && (
        <MedicationEditorModal
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={close}
          onSave={save}
          onDelete={remove}
        />
      )}

      <VoiceMARModal
        open={voiceMAROpen}
        clientId={clientId}
        dateStr={dateStr}
        onClose={() => setVoiceMAROpen(false)}
        onApplied={load}
      />
    </div>
  );
}
